import React, { useEffect, useState } from 'react';
import { Check, X, ShieldCheck, Truck, Star, Activity } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TestimonialCard } from '../components/TestimonialCard';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
import { StickyCTA } from '../components/StickyCTA';
import { CommentCard } from '../components/CommentCard';
export function AdvertorialPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToOffer = () => {
    window.location.href = 'https://www.tryglisera.com/pages/glisera%E2%84%A2-cyclecalm';
  };
  return <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-[#1F3A2A]/10 selection:text-[#1F3A2A]" data-id="element-71">
      {/* Navigation / Header Bar */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40" data-id="element-72">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-73">
          <div className="font-bold text-xl tracking-tight text-slate-900" data-id="element-74">
            Women's Health <span className="text-[#1F3A2A]" data-id="element-75">Daily</span>
          </div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider border border-slate-200 px-2 py-1 rounded" data-id="element-76">
            Advertorial
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12" data-id="element-77">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8" data-id="element-78">
          {/* Main Article Content */}
          <main data-id="element-79">
            {/* Article Meta */}
            <div className="mb-8 border-b border-slate-200 pb-8" data-id="element-80">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4" data-id="element-81">
                <span className="font-bold text-[#1F3A2A] uppercase tracking-wide text-xs" data-id="element-82">
                  Women's Health
                </span>
                <span data-id="element-83">•</span>
                <span data-id="element-84">February 2026</span>
                <span data-id="element-85">•</span>
                <span data-id="element-86">142,847 views</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight" data-id="element-87">
                Why Your Migraine Meds Stop Working 2 Days Before Your Period
              </h1>

              <p className="text-xl text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-88">
                Neurologist explains: The real reason hormonal migraines don't
                respond to treatment—and what to do about it.
              </p>

              <div className="flex items-center gap-3" data-id="element-89">
                <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=100&h=100&q=80" alt="Dr. Rachel Simmons" className="w-12 h-12 rounded-full object-cover border-2 border-[#1F3A2A]/20" data-id="element-90" />
                <div data-id="element-91">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-92">
                    By Dr. Rachel Simmons
                  </div>
                  <div className="text-slate-500 text-xs" data-id="element-93">
                    Board-Certified Neurologist
                  </div>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <article className="prose prose-lg prose-slate max-w-none font-serif" data-id="element-94">
              <p data-id="element-95">
                The pounding headache that shows up like clockwork before your
                period...
              </p>
              <p data-id="element-96">The one that laughs at your triptans...</p>
              <p data-id="element-97">The one that turns ibuprofen into sugar pills...</p>
              <p data-id="element-98">
                The one that steals 2-3 days from your life every single
                month...
              </p>
              <p data-id="element-99">
                If you've ever wondered why your period migraines are so much
                harder to treat than your regular headaches, keep reading.
              </p>
              <p className="font-bold text-slate-900" data-id="element-100">
                Because what I'm about to share with you could change
                everything.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/5hnQ56nVmQejNh1LXVDZ6C/ChatGPT_Image_1_Φεβ_2026,_08_07_55_μ.μ..png" alt="Woman suffering from migraine, holding her head in pain" className="w-full rounded-xl my-8 shadow-lg" data-id="element-101" />

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-102">
                I Know This Because I've Treated Thousands of Women With the
                Same Problem
              </h2>

              <p data-id="element-103">
                My name is Dr. Rachel Simmons. I'm a board-certified neurologist
                with 18 years of experience treating migraines.
              </p>
              <p data-id="element-104">
                And for most of my career, I was just as frustrated as you are.
              </p>
              <p data-id="element-105">
                I'd watch patient after patient come in with the same story:
              </p>
              <div className="bg-[#1F3A2A]/5 border-l-4 border-[#1F3A2A] p-6 my-8 not-prose rounded-r-lg" data-id="element-106">
                <p className="font-serif text-lg text-[#1F3A2A] italic font-medium m-0" data-id="element-107">
                  "My regular migraines respond to medication. But the ones
                  around my period? Nothing touches them."
                </p>
              </div>
              <p data-id="element-108">I'd prescribe triptans. Different doses. Different brands.</p>
              <p data-id="element-109">I'd suggest magnesium, riboflavin, CoQ10...</p>
              <p data-id="element-110">I'd even recommend hormonal birth control adjustments.</p>
              <p data-id="element-111">Some women got partial relief. Most didn't.</p>
              <p data-id="element-112">And I couldn't figure out why.</p>
              <p data-id="element-113">
                Until I came across a study that changed everything I thought I
                knew about hormonal migraines.
              </p>

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-114">
                The Shocking Truth About Period Migraines (That Most Doctors
                Miss)
              </h2>

              <p data-id="element-115">Here's what the research revealed:</p>
              <p data-id="element-116">
                Hormonal migraines don't start when you feel them. They start
                6-12 hours BEFORE.
              </p>
              <p data-id="element-117">Let me explain.</p>
              <p data-id="element-118">
                Between days 25-28 of your cycle—right before your period—your
                estrogen doesn't just drop.
              </p>
              <p className="font-bold text-slate-900" data-id="element-119">It crashes.</p>
              <p data-id="element-120">Doctors call this the "Estrogen Crash Window."</p>

              <img src="https://cdn.magicpatterns.com/uploads/5Knh5eu8SJZPp13ZVwRWgb/ChatGPT_Image_1_Φεβ_2026,_08_07_53_μ.μ..png" alt="Estrogen Crash Window diagram showing hormone levels during menstrual cycle" className="w-full rounded-xl my-8 shadow-lg" data-id="element-121" />

              <p data-id="element-122">
                When estrogen crashes this fast, it triggers a cascade of events
                in your brain:
              </p>
              <ul className="list-none pl-0 space-y-3 my-6 not-prose" data-id="element-123">
                <li className="flex items-start gap-3" data-id="element-124">
                  <Activity className="text-[#1F3A2A] flex-shrink-0 mt-1" data-id="element-125" />
                  <span className="text-slate-700" data-id="element-126">
                    Your blood vessels start swelling and constricting in
                    waves...
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-127">
                  <Activity className="text-[#1F3A2A] flex-shrink-0 mt-1" data-id="element-128" />
                  <span className="text-slate-700" data-id="element-129">
                    Inflammation builds at the base of your skull and temples...
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-130">
                  <Activity className="text-[#1F3A2A] flex-shrink-0 mt-1" data-id="element-131" />
                  <span className="text-slate-700" data-id="element-132">
                    Pressure mounts behind your eyes...
                  </span>
                </li>
              </ul>
              <p data-id="element-133">And all of this happens while you're sleeping.</p>
              <p data-id="element-134">
                By the time you wake up with that familiar throbbing? The
                inflammatory storm has been raging for 6-12 hours already.
              </p>

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-135">
                This Is Why Your Medications Don't Work
              </h2>

              <p data-id="element-136">Think about it:</p>
              <p data-id="element-137">You feel the migraine at 8am.</p>
              <p data-id="element-138">You take your triptan at 8:15am.</p>
              <p data-id="element-139">But the inflammation started at 2am.</p>
              <p data-id="element-140">
                You're not treating the migraine. You're treating the aftermath.
              </p>
              <p data-id="element-141">
                It's like calling the fire department after your house has
                already burned down.
              </p>
              <p data-id="element-142">This is why:</p>

              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 my-8 not-prose space-y-4" data-id="element-143">
                <div className="flex gap-3" data-id="element-144">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-145" />
                  <span className="text-slate-700" data-id="element-146">
                    <strong data-id="element-147">Triptans barely touch period migraines</strong> —
                    they're designed to stop a migraine in progress, not reverse
                    6-12 hours of inflammation
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-148">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-149" />
                  <span className="text-slate-700" data-id="element-150">
                    <strong data-id="element-151">Ibuprofen feels useless</strong> — by the time you
                    take it, the inflammatory cascade is already complete
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-152">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-153" />
                  <span className="text-slate-700" data-id="element-154">
                    <strong data-id="element-155">
                      Birth control helps some women but not others
                    </strong>{' '}
                    — it addresses hormone levels, not the timing of
                    intervention
                  </span>
                </div>
                <div className="flex gap-3" data-id="element-156">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-157" />
                  <span className="text-slate-700" data-id="element-158">
                    <strong data-id="element-159">
                      Supplements take the edge off but don't prevent it
                    </strong>{' '}
                    — they support your system but don't intercept the crash
                  </span>
                </div>
              </div>

              <p className="font-bold text-slate-900 text-xl" data-id="element-160">
                The problem was never your medication. The problem was TIMING.
              </p>

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-161">
                What If You Could Intercept the Migraine BEFORE It Started?
              </h2>

              <p data-id="element-162">
                Once I understood the Estrogen Crash Window, the solution became
                obvious:
              </p>
              <p data-id="element-163">
                You don't treat period migraines when you feel them. You treat
                them BEFORE the crash.
              </p>
              <p data-id="element-164">The question was: how?</p>
              <p data-id="element-165">Pills take too long to absorb.</p>
              <p data-id="element-166">Injections aren't practical for monthly use.</p>
              <p data-id="element-167">
                I needed something that could deliver relief directly to the
                source—the blood vessels at your temples and the base of your
                skull—during that critical 24-48 hour window before your period.
              </p>
              <p data-id="element-168">
                That's when I discovered Glisera™ Migraine Relief CycleCalm.
              </p>

              <div className="my-12 not-prose" data-id="element-169">
                <h3 className="font-sans font-bold text-3xl md:text-4xl text-slate-900 text-center mb-4" data-id="element-170">
                  Introducing CycleCalm: The First Migraine Relief Designed for
                  Your Cycle
                </h3>

                <div className="flex justify-center mb-8" data-id="element-171">
                  <img src="https://cdn.magicpatterns.com/uploads/hKfj9n7ERxaXex1sJ8p5fW/ChatGPT_Image_1_Φεβ_2026,_08_07_49_μ.μ..png" alt="Glisera CycleCalm Migraine Relief Roll-On product" className="w-full max-w-md rounded-xl shadow-lg" data-id="element-172" />
                </div>

                <p className="text-lg text-slate-700 mb-6" data-id="element-173">
                  CycleCalm isn't like other migraine roll-ons.
                </p>
                <p className="text-slate-700 mb-6" data-id="element-174">
                  Most topical products are basically aromatherapy—peppermint
                  oil that gives you 10 minutes of cooling sensation before the
                  migraine comes roaring back.
                </p>
                <p className="text-slate-700 mb-6" data-id="element-175">CycleCalm is different.</p>
                <p className="text-slate-700 mb-6" data-id="element-176">
                  It uses bioactive cooling compounds that penetrate through
                  your skin and reach the blood vessels underneath—the exact
                  vessels that swell during the estrogen crash.
                </p>
                <p className="text-slate-700 mb-6" data-id="element-177">
                  When you apply it to your temples, forehead, and the base of
                  your skull 24-48 hours before your period (or at the first
                  sign of "the drop"), you're doing something revolutionary:
                </p>
                <p className="font-bold text-slate-900 text-lg mb-6" data-id="element-178">
                  You're calming the inflammation BEFORE it spirals out of
                  control.
                </p>
                <p className="text-slate-700 mb-6" data-id="element-179">
                  Instead of chasing pain... You're preventing it.
                </p>
                <p className="text-slate-700" data-id="element-180">
                  Instead of fighting a wildfire... You're putting out the first
                  spark.
                </p>
              </div>

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-181">
                Here's How It Works
              </h2>

              <div className="grid md:grid-cols-3 gap-6 not-prose my-8" data-id="element-182">
                <div className="bg-[#1F3A2A]/5 p-6 rounded-xl border border-[#1F3A2A]/10" data-id="element-183">
                  <img src="https://cdn.magicpatterns.com/uploads/852HfMAnWmx1ruBer6FmNB/ChatGPT_Image_1_Φεβ_2026,_08_05_15_μ.μ..png" alt="Woman tracking her cycle on phone app" className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-184" />
                  <div className="bg-[#1F3A2A]/10 w-10 h-10 rounded-full flex items-center justify-center text-[#1F3A2A] font-bold mb-4" data-id="element-185">
                    1
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-186">
                    Track your cycle
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-187">
                    (You probably already do this.)
                  </p>
                </div>
                <div className="bg-[#1F3A2A]/5 p-6 rounded-xl border border-[#1F3A2A]/10" data-id="element-188">
                  <img src="https://cdn.magicpatterns.com/uploads/v91d98ZbTfdCBDVgdriTTc/ChatGPT_Image_1_Φεβ_2026,_08_05_18_μ.μ..png" alt="Woman applying CycleCalm roll-on to temple" className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-189" />
                  <div className="bg-[#1F3A2A]/10 w-10 h-10 rounded-full flex items-center justify-center text-[#1F3A2A] font-bold mb-4" data-id="element-190">
                    2
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-191">
                    Apply CycleCalm
                  </h4>
                  <p className="text-sm text-slate-600" data-id="element-192">
                    To temples, forehead, and neck 1-2 days before your period
                    starts—or as soon as you feel "the drop".
                  </p>
                </div>
                <div className="bg-[#1F3A2A]/5 p-6 rounded-xl border border-[#1F3A2A]/10" data-id="element-193">
                  <img src="https://cdn.magicpatterns.com/uploads/3X7Ndnd84iD9wHLcpbKKX4/ChatGPT_Image_1_Φεβ_2026,_08_05_16_μ.μ..png" alt="Woman feeling relieved and comfortable" className="w-full aspect-square object-cover rounded-lg mb-4" data-id="element-194" />
                  <div className="bg-[#1F3A2A]/10 w-10 h-10 rounded-full flex items-center justify-center text-[#1F3A2A] font-bold mb-4" data-id="element-195">
                    3
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-196">Reapply</h4>
                  <p className="text-sm text-slate-600" data-id="element-197">
                    As needed during your vulnerable window.
                  </p>
                </div>
              </div>

              <p data-id="element-198">That's it.</p>
              <p data-id="element-199">No pills to swallow. No prescriptions. No side effects.</p>
              <p data-id="element-200">
                Just targeted relief, delivered directly to the source, at
                exactly the right time.
              </p>

              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-201">
                Women Are Finally Getting Their Lives Back
              </h2>

              <p data-id="element-202">
                Since recommending CycleCalm to my patients, I've heard stories
                that still give me chills:
              </p>

              <div className="not-prose grid gap-6" data-id="element-203">
                <TestimonialCard headline="For the first time in 5 years, I didn't lose a single day." quote="I've tried everything. Triptans, birth control, supplements, elimination diets. Nothing worked for my period migraines. My doctor just kept telling me to 'take ibuprofen earlier.' Super helpful, right? I started using CycleCalm two days before my period for two cycles in a row. And for the first time in maybe five years, I made it through without canceling a single thing. I actually cried. I forgot what it felt like to trust my own body." author="Michelle T." location="Ohio, 41" avatarUrl="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-204" />
                <TestimonialCard headline="My weekends aren't a coin flip anymore." quote="My period always hits on weekends. So for three years, every Saturday was a gamble—would I make it to my nephew's birthday party, or would I be hiding in a dark room? The first month I used CycleCalm, I went to the whole party. No ice pack. No dark room. Just a normal Saturday. If you know what these migraines steal from you, you know how big that is." author="Danielle R." location="Texas, 38" avatarUrl="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-205" />
                <TestimonialCard headline="I'm a nurse. I don't fall for marketing. But this actually works." quote="I'm not the type to believe health product ads. I know how marketing works. But I also know what it's like to lose 2-3 days every month to migraines that nothing touches. It's not magic. I still get a little tension sometimes. But the full-blown, can't-function, hiding-in-the-dark migraine? That hasn't happened in three cycles. For me, that's everything." author="Karen L." location="California, 44" avatarUrl="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-206" />
              </div>

              <div id="offer-section" className="bg-white border-2 border-[#1F3A2A] rounded-2xl p-6 md:p-10 shadow-2xl my-12 not-prose relative overflow-hidden" data-id="element-207">
                <div className="absolute top-0 right-0 bg-[#1F3A2A] text-white text-xs font-bold px-4 py-1 rounded-bl-lg" data-id="element-208">
                  LIMITED TIME OFFER
                </div>

                <h2 className="font-sans font-bold text-3xl text-slate-900 mb-2 text-center" data-id="element-209">
                  Special Offer: Save 30% Today
                </h2>
                <p className="text-center text-slate-600 mb-8" data-id="element-210">
                  Right now, readers can try Glisera™ Migraine Relief CycleCalm
                  and save 30% on their first order.
                </p>

                <div className="flex flex-col md:flex-row gap-8 items-center mb-8" data-id="element-211">
                  <div className="flex-1" data-id="element-212">
                    <ul className="space-y-4" data-id="element-213">
                      <li className="flex items-start gap-3" data-id="element-214">
                        <div className="bg-[#1F3A2A]/10 p-1 rounded-full mt-0.5" data-id="element-215">
                          <Check size={16} className="text-[#1F3A2A]" data-id="element-216" />
                        </div>
                        <span className="text-slate-700 text-sm" data-id="element-217">
                          <strong data-id="element-218">It's completely risk-free.</strong> CycleCalm
                          comes with a 60-day money-back guarantee. If it
                          doesn't help during your next cycle, you get a full
                          refund. No questions asked.
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-219">
                        <div className="bg-[#1F3A2A]/10 p-1 rounded-full mt-0.5" data-id="element-220">
                          <Check size={16} className="text-[#1F3A2A]" data-id="element-221" />
                        </div>
                        <span className="text-slate-700 text-sm" data-id="element-222">
                          <strong data-id="element-223">You already know when your window is.</strong>{' '}
                          If you can predict your migraine, you can finally use
                          that knowledge to get ahead of it.
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-224">
                        <div className="bg-[#1F3A2A]/10 p-1 rounded-full mt-0.5" data-id="element-225">
                          <Check size={16} className="text-[#1F3A2A]" data-id="element-226" />
                        </div>
                        <span className="text-slate-700 text-sm" data-id="element-227">
                          <strong data-id="element-228">You've tried everything else.</strong> This is
                          the one approach designed specifically for hormonal
                          migraines—working WITH your cycle instead of against
                          it.
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex-1 w-full" data-id="element-229">
                    <Button fullWidth size="xl" onClick={scrollToOffer} className="mb-3 shadow-[#1F3A2A]/20 shadow-xl" data-id="element-230">
                      CLAIM YOUR 30% DISCOUNT NOW
                    </Button>
                    <div className="flex items-center justify-center gap-4 text-xs text-slate-500" data-id="element-231">
                      <span className="flex items-center gap-1" data-id="element-232">
                        <Truck size={12} data-id="element-233" /> Free U.S. Shipping
                      </span>
                      <span className="flex items-center gap-1" data-id="element-234">
                        <ShieldCheck size={12} data-id="element-235" /> 60-Day Guarantee
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-500 italic" data-id="element-236">
                  Don't spend another month losing days to migraines you can
                  predict but can't stop.
                </p>
              </div>

              {/* Social Proof Comments Section */}
              <div className="mt-12 pt-8 border-t border-slate-200 not-prose" data-id="element-237">
                <h3 className="font-sans font-bold text-xl text-slate-900 mb-6" data-id="element-238">
                  What Other Women Are Saying:
                </h3>

                <div className="space-y-2" data-id="element-239">
                  <CommentCard name="Linda M." comment="Just ordered mine. The explanation about WHY my triptans don't work finally makes sense. Fingers crossed for next month! 🤞" likes={47} timeAgo="2h" avatarUrl="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-240" />
                  <CommentCard name="Stephanie V." comment="Been using this for 2 months. Was skeptical at first but went from losing 2-3 days to maybe a few hours of mild pressure. That's huge for me." likes={83} timeAgo="3h" avatarUrl="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-241" />
                  <CommentCard name="Rachel K." comment="Why did my neurologist never explain ANY of this?? She just kept switching my meds. Thank you for finally making it make sense." likes={61} timeAgo="4h" avatarUrl="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-242" />
                  <CommentCard name="Maria S." comment="Does this work for perimenopause migraines? Mine have gotten SO much worse since 45." likes={29} timeAgo="4h" avatarUrl="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-243" />
                  <CommentCard name="Amanda C." comment="Yes! I'm 47 and my migraines went crazy during peri. CycleCalm has been a lifesaver. Definitely worth trying." likes={34} timeAgo="3h" isReply avatarUrl="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-244" />
                  <CommentCard name="Jennifer B." comment="Ordered the 2-pack. One for home, one for my purse. Already dreading my period less lol" likes={52} timeAgo="5h" avatarUrl="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-245" />
                  <CommentCard name="Theresa N." comment="Finally my cycle tracking is useful for something besides predicting misery 😂" likes={71} timeAgo="6h" avatarUrl="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-246" />
                </div>

                <div className="mt-8 text-center" data-id="element-247">
                  <Button onClick={scrollToOffer} size="lg" data-id="element-248">
                    CLAIM YOUR 30% DISCOUNT NOW
                  </Button>
                </div>
              </div>
            </article>
          </main>

          {/* Sticky Sidebar CTA - Desktop Only */}
          <aside className="hidden lg:block" data-id="element-249">
            <StickyCTA onCtaClick={scrollToOffer} data-id="element-250" />
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-12 px-4" data-id="element-251">
        <div className="max-w-3xl mx-auto text-center" data-id="element-252">
          <div className="text-[10px] text-slate-400 space-y-4 leading-relaxed" data-id="element-253">
            <p data-id="element-254">
              This is an advertorial and not an actual news article, blog, or
              consumer protection update.
            </p>
            <p data-id="element-255">
              Results may vary. This website is not intended to provide medical
              advice. Consult your doctor regarding treatment of medical
              conditions.
            </p>
            <div className="flex justify-center gap-4 pt-4" data-id="element-256">
              <a href="#" className="hover:text-slate-600" data-id="element-257">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-258">
                Terms of Service
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-259">
                Contact Us
              </a>
            </div>
            <p className="pt-4" data-id="element-260">© 2026 Glisera Health. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#1F3A2A] shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.15)] transition-transform duration-300 z-50 lg:hidden ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-261">
        <div className="p-4" data-id="element-262">
          <div className="flex items-center justify-between mb-3" data-id="element-263">
            <div data-id="element-264">
              <p className="font-bold text-sm text-slate-900" data-id="element-265">
                Stop Period Migraines
              </p>
              <div className="flex items-center gap-1" data-id="element-266">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="#FCD34D" className="text-yellow-400" data-id="element-267" />)}
                <span className="text-xs text-slate-600 ml-1" data-id="element-268">4.9 (2,847)</span>
              </div>
            </div>
            <div className="bg-[#1F3A2A] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-269">
              30% OFF
            </div>
          </div>
          <Button fullWidth onClick={scrollToOffer} size="lg" data-id="element-270">
            CLAIM DISCOUNT →
          </Button>
          <p className="text-[10px] text-center text-slate-500 mt-2" data-id="element-271">
            60-Day Guarantee • Free Shipping
          </p>
        </div>
      </div>
    </div>;
}